import { useCallback, useEffect, useState } from 'react';

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Resvg, initWasm } from '@resvg/resvg-wasm';
import ReactDOMServer from 'react-dom/server';

import ResvgSource from '@/assets/web-assemblies/resvg.wasm';

import { RateBucketSVG } from './RateBucket';

type RateBucketPdfProps = {
  title: string;
  percent: number;
  index: number;
};

export const RateBucketPdf: React.FC<RateBucketPdfProps> = (props) => {
  return (
    <View style={styles.container}>
      <View>
        <SvgToPngImage
          width={82}
          height={82}
          svgContent={
            <RateBucketSVG percent={props.percent} index={props.index} />
          }
        />
      </View>
      <View style={styles.textContainer}>
        <Text color="black" opacity={1} fill="black" style={styles.title}>
          {props.title}
        </Text>
        <Text style={styles.percent}>{props.percent.toPrecision(2)}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  textContainer: {
    width: 80,
    height: 80,
    position: 'absolute',
    alignItems: 'center',
    direction: 'column',
    top: '62%',
    left: '62%',
    transform: 'translate(-50%,-50%)',
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  title: {
    fontSize: 7,
    textAlign: 'center',
    fontWeight: 'medium',
    zIndex: 50,
    color: 'black',
  },
  percent: {
    textAlign: 'center',
    fontSize: 14,
    marginTop: 2,
    zIndex: 50,
    color: 'black',
  },
});

export const initWasmForPdf = async () => {
  await initWasm(fetch(ResvgSource));
};

export const svgToPngDataUrl = async (svgContent: string) => {
  try {
    const resvg = new Resvg(svgContent, {
      fitTo: { mode: 'width', value: 500 },
    });
    const pngData = resvg.render();
    const pngBlob = pngData.asPng();

    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([pngBlob], { type: 'image/png' }));
    });

    return await base64Promise;
  } catch (error) {
    console.error('Error converting SVG to PNG:', error);
    return null;
  }
};

type SvgToPngImageProps = {
  svgContent: React.ReactNode;
  width: number;
  height: number;
};

// Image tag of react-pdf did not support SVG base 64, so we had to change it to png base 64
const SvgToPngImage: React.FC<SvgToPngImageProps> = ({
  svgContent,
  width,
  height,
}) => {
  const [pngDataUrl, setPngDataUrl] = useState<string>();

  const loadPng = useCallback(async () => {
    if (pngDataUrl != undefined) return;

    await initWasmForPdf();
    const pngUrl = (await svgToPngDataUrl(
      ReactDOMServer.renderToString(svgContent)
    )) as string;
    setPngDataUrl(pngUrl);
  }, [svgContent, pngDataUrl]);

  useEffect(() => {
    loadPng();
  }, [loadPng]);

  if (!pngDataUrl) return <p>Loading...</p>;

  return <Image src={pngDataUrl} style={{ width, height }} />;
};
